<template>
  <Teleport to="body">
    <transition name="icon-tooltip">
      <div
        v-if="tooltip"
        :id="TOOLTIP_ID"
        class="icon-tooltip"
        :class="`icon-tooltip--${tooltip.placement}`"
        role="tooltip"
        :style="{ top: `${tooltip.top}px`, left: `${tooltip.left}px` }"
      >
        {{ tooltip.text }}
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from "vue";

/**
 * Infobulle globale des boutons d'action à icône (.btn-icon) et des éléments [data-tooltip].
 * Le texte vient de data-tooltip, sinon de title (repris pour éviter l'infobulle native), sinon de aria-label.
 */
const SELECTOR = ".btn-icon, [data-tooltip]";
const TOOLTIP_ID = "icon-tooltip";
const GAP = 8;

const tooltip = ref(null);
let current = null;

const textOf = (element) => {
  const title = element.getAttribute("title");
  if (title) {
    element.dataset.tooltip = title;
    element.removeAttribute("title");
  }
  return element.dataset.tooltip || element.getAttribute("aria-label") || "";
};

const show = (element) => {
  const text = textOf(element);
  if (!text) return;
  const rect = element.getBoundingClientRect();
  const placement = rect.top < 48 ? "bottom" : "top";
  current = element;
  element.setAttribute("aria-describedby", TOOLTIP_ID);
  tooltip.value = {
    text,
    placement,
    top: placement === "top" ? rect.top - GAP : rect.bottom + GAP,
    left: Math.min(Math.max(rect.left + rect.width / 2, 60), window.innerWidth - 60),
  };
};

const hide = () => {
  current?.removeAttribute("aria-describedby");
  current = null;
  tooltip.value = null;
};

const onEnter = (event) => {
  const element = event.target instanceof Element ? event.target.closest(SELECTOR) : null;
  if (element && element !== current) show(element);
};

const onLeave = (event) => {
  if (!current) return;
  const next = event.relatedTarget;
  if (next instanceof Node && current.contains(next)) return;
  hide();
};

const onKey = (event) => {
  if (event.key === "Escape") hide();
};

onMounted(() => {
  document.addEventListener("mouseover", onEnter);
  document.addEventListener("mouseout", onLeave);
  document.addEventListener("focusin", onEnter);
  document.addEventListener("focusout", onLeave);
  document.addEventListener("click", hide, true);
  document.addEventListener("keydown", onKey);
  window.addEventListener("scroll", hide, true);
});

onBeforeUnmount(() => {
  document.removeEventListener("mouseover", onEnter);
  document.removeEventListener("mouseout", onLeave);
  document.removeEventListener("focusin", onEnter);
  document.removeEventListener("focusout", onLeave);
  document.removeEventListener("click", hide, true);
  document.removeEventListener("keydown", onKey);
  window.removeEventListener("scroll", hide, true);
});
</script>

<style>
.icon-tooltip {
  position: fixed;
  z-index: 2300;
  max-width: 240px;
  padding: 0.35rem 0.65rem;
  border-radius: calc(var(--radius) - 2px);
  background: hsl(var(--foreground));
  color: hsl(var(--background));
  font-size: 0.78rem;
  font-weight: 500;
  line-height: 1.3;
  text-align: center;
  pointer-events: none;
  box-shadow: var(--shadow-sm);
  transform: translate(-50%, -100%);
}
.icon-tooltip--bottom {
  transform: translate(-50%, 0);
}
.icon-tooltip::after {
  content: "";
  position: absolute;
  left: 50%;
  width: 8px;
  height: 8px;
  background: inherit;
  transform: translateX(-50%) rotate(45deg);
}
.icon-tooltip--top::after {
  bottom: -4px;
}
.icon-tooltip--bottom::after {
  top: -4px;
}
.icon-tooltip-enter-active,
.icon-tooltip-leave-active {
  transition: opacity 0.12s ease;
}
.icon-tooltip-enter-from,
.icon-tooltip-leave-to {
  opacity: 0;
}
</style>
